import { type RefObject, useEffect } from 'react'

interface UseInfiniteScrollTriggerOptions {
  scrollRef: RefObject<HTMLElement | null>
  hasNextPage: boolean
  isFetchingNextPage: boolean
  fetchNextPage: () => unknown
  thresholdPx?: number
  /** Re-evaluated triggers re-check (e.g. when scroll area height changes or list length changes). */
  recomputeKey?: unknown
}

const DEFAULT_THRESHOLD_PX = 240

/**
 * Loads next page when the scroll container is within `thresholdPx` of its bottom.
 * Also fires once when the dependencies change, so a short list that already fits triggers a fetch.
 */
export function useInfiniteScrollTrigger({
  scrollRef,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  thresholdPx = DEFAULT_THRESHOLD_PX,
  recomputeKey,
}: UseInfiniteScrollTriggerOptions): void {
  useEffect(() => {
    const root = scrollRef.current
    if (!root) return

    const check = (): void => {
      if (!hasNextPage || isFetchingNextPage) return
      const distanceFromBottom = root.scrollHeight - root.scrollTop - root.clientHeight
      if (distanceFromBottom <= thresholdPx) {
        fetchNextPage()
      }
    }

    root.addEventListener('scroll', check, { passive: true })
    check()
    return () => root.removeEventListener('scroll', check)
  }, [scrollRef, hasNextPage, isFetchingNextPage, fetchNextPage, thresholdPx, recomputeKey])
}
